<script setup lang="ts">
import { register, SwiperContainer } from 'swiper/element/bundle';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import { useCartShipping } from '@/shared/composables/cart/useCartShipping';
import { useGiftAvailability } from '@/shared/composables/cart/useGiftAvailability';
import { useGiftProducts } from '@/shared/composables/cart/useGiftProducts';
import {
  breakpoints,
  posthogEvents,
  userCookies
} from '@/shared/constants/constants';
import { useCartStore } from '@/shared/pinia/modules/cart';
import { getCookie, setCookie } from '@/shared/utils/cookie';
import { notifyBugsnagWithContext } from '@/shared/utils/error';

import RewardProductSlide from './RewardProductSlide.vue';

register();

type Props = {
  collectionHandle: string;
  giftThreshold: number;
  shippingThreshold: number;
  maxProducts?: number;
  isMainCart?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  maxProducts: 3,
  isMainCart: false
});

const cartStore = useCartStore();
const { shippingSubtotal, totalCartLevelDiscounts } = useCartShipping();
const isAddingGift = ref(false);
const swiperEl = ref<SwiperContainer | null>(null);
const isSwiperInitialized = ref(false);
const windowWidth = ref(
  typeof window !== 'undefined' ? window.innerWidth : 1200
);

// Track window width for responsive behavior
const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth;
    window.addEventListener('resize', updateWindowWidth);
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateWindowWidth);
  }
});

// Computed to determine if we should use swiper (for main cart: only when < 1280px)
const shouldUseSwiper = computed(() => {
  if (!props.isMainCart) {
    return true; // Always use swiper for mini cart
  }
  return windowWidth.value < breakpoints.xxl; // For main cart, use swiper only when < 1280px
});

const { products, loading: isLoading } = useGiftProducts(
  props.collectionHandle,
  props.maxProducts
);

// Market-aware availability - reuses the shared storefront fetch, no extra request.
const { hasAvailableGifts } = useGiftAvailability(props.collectionHandle);

// Utility function to wait for swiper element to be ready
const waitForSwiperElement = async (
  maxAttempts = 20,
  delay = 50
): Promise<boolean> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, delay));

    if (swiperEl.value) {
      // Element exists in DOM - that's enough to proceed with initialization
      // Dimensions will be fixed after initialization via fixSwiperHeight
      return true;
    }
  }
  return false;
};

// Fix zero-height issue by waiting for swiper instance and forcing update
// This handles the case where shadow DOM content isn't laid out immediately after initialization
const fixSwiperHeight = (attempt = 0): void => {
  if (!swiperEl.value) return;

  const container = swiperEl.value;
  const swiperInstance = container.swiper;

  if (swiperInstance) {
    // Force swiper to recalculate dimensions
    swiperInstance.update();
    swiperInstance.updateSize();
    swiperInstance.updateSlides();

    // Check if we have valid dimensions now
    // Use requestAnimationFrame to ensure layout has completed
    requestAnimationFrame(() => {
      if (!swiperEl.value) return;

      const currentHeight = swiperEl.value.offsetHeight;
      const currentWidth = swiperEl.value.offsetWidth;

      // If still zero height and we haven't tried too many times, retry
      // But also check width - if width is 0, the element might not be visible at all
      if (currentHeight < 10 && currentWidth > 0 && attempt < 15) {
        // Element is visible (has width) but no height - retry
        setTimeout(() => fixSwiperHeight(attempt + 1), 100);
      } else if (currentWidth === 0 && attempt < 10) {
        // Element might not be visible yet - wait a bit longer
        setTimeout(() => fixSwiperHeight(attempt + 1), 150);
      }
    });
  } else if (attempt < 30) {
    // Swiper instance not ready yet, wait and retry
    // Increased max attempts since shadow DOM initialization can be slow
    setTimeout(() => fixSwiperHeight(attempt + 1), 50);
  }
};

const initSwiper = async () => {
  if (!swiperEl.value) return;

  // If already initialized, just update size
  if (isSwiperInitialized.value && swiperEl.value.swiper) {
    swiperEl.value.swiper.update();
    swiperEl.value.swiper.updateSize();
    return;
  }

  if (isSwiperInitialized.value) return;

  const breakpointsConfig: Record<number, Record<string, unknown>> = {
    0: {
      slidesPerView: 1.15,
      spaceBetween: 8,
      slidesOffsetBefore: 8,
      slidesOffsetAfter: 8
    },
    512: {
      slidesPerView: 1.8,
      spaceBetween: 8,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16
    }
  };

  swiperEl.value.breakpoints = breakpointsConfig;

  swiperEl.value.freeMode = true;
  // cssMode delegates to native CSS scroll-snap, which iOS Safari handles
  // reliably inside the mini cart's nested scroll context. Desktop keeps
  // Swiper's JS mode so mousewheel forceToAxis and drag-to-scroll still work.
  swiperEl.value.cssMode = window.matchMedia('(pointer: coarse)').matches;
  swiperEl.value.mousewheel = {
    forceToAxis: true
  };
  swiperEl.value.injectStyles = [
    `
    :host .swiper-wrapper {
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    }
    `
  ];

  try {
    swiperEl.value.initialize();
    isSwiperInitialized.value = true;

    // Fix zero-height issue - wait for swiper instance to be ready
    // Use requestAnimationFrame to ensure DOM has updated after initialize()
    requestAnimationFrame(() => {
      // Small delay to allow shadow DOM to start rendering
      setTimeout(() => fixSwiperHeight(), 50);
    });
  } catch (error) {
    console.error('Error initializing swiper:', error);
    isSwiperInitialized.value = false;
  }
};

// Check if a _pickedReward property is set to true for any product in the cart
const hasGiftInCart = computed(() => {
  if (!cartStore.cart) return false;

  return (cartStore.cart.items || []).some(
    (item) => item.properties?._pickedReward === 'true'
  );
});

// Calculate if gift threshold is achieved
const shouldShowGifts = computed(() => {
  if (!cartStore.cart) {
    return false;
  }

  const effectiveSubtotal =
    shippingSubtotal.value - totalCartLevelDiscounts.value;
  const thresholdMet = effectiveSubtotal >= props.giftThreshold;

  return thresholdMet && !hasGiftInCart.value;
});

// Gate the picker on market-aware availability - render nothing (not the "no
// available gifts" message) when no purchasable gift survives the fetch.
const canShowGifts = computed(
  () => shouldShowGifts.value && hasAvailableGifts.value
);

// Single computed that determines if swiper should be initialized
const shouldInitSwiper = computed(() => {
  return (
    canShowGifts.value &&
    shouldUseSwiper.value &&
    !isLoading.value &&
    products.value.length > 0
  );
});

const handleAddToCart = async (
  variantId: number,
  productId: string,
  productTitle: string,
  index: number
) => {
  if (isAddingGift.value) return;

  try {
    isAddingGift.value = true;

    const cartItem = {
      id: variantId,
      quantity: 1,
      properties: {
        _pickedReward: 'true'
      }
    };

    await cartStore.addIndependentProductsToCart([cartItem]);

    window.posthog?.capture(posthogEvents.GIFT_SELECTED, {
      product_id: productId,
      variant_id: variantId,
      product_name: productTitle,
      index: index
    });

    // Wait a bit before re-enabling to prevent double-clicks
    setTimeout(() => {
      isAddingGift.value = false;
    }, 2000);
  } catch (error) {
    notifyBugsnagWithContext('GiftCollectionDisplay.handleAddToCart', error, {
      message: 'Failed to add to cart',
      data: { variantId, productId, productTitle, index }
    });
    isAddingGift.value = false;
  }
};

// Track when gift picker is shown - wait for products to load before evaluating
watch(
  [isLoading, canShowGifts],
  ([loading, showGifts]) => {
    if (
      !loading &&
      showGifts &&
      !getCookie(userCookies.REWARDS_VISIBILITY_ALREADY_TRACKED)
    ) {
      window.posthog?.capture(posthogEvents.GIFT_PICKER_SHOWN);
      setCookie(
        userCookies.REWARDS_VISIBILITY_ALREADY_TRACKED,
        'true',
        'session'
      );
    }
  },
  { immediate: true }
);

watch(
  shouldInitSwiper,
  async (shouldInit) => {
    if (shouldInit) {
      // Wait for DOM to update
      await nextTick();

      // Reset initialization flag when component becomes visible
      // This ensures swiper gets re-initialized properly when conditions change
      isSwiperInitialized.value = false;

      // Wait for element to be ready (handles conditional rendering and async data loading)
      const elementReady = await waitForSwiperElement();

      if (elementReady && swiperEl.value) {
        await initSwiper();
      }
    } else {
      // Component hidden or swiper not needed - reset state
      // This allows re-initialization when shown again (e.g., when conditions change)
      isSwiperInitialized.value = false;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <div v-if="canShowGifts" class="overflow-hidden">
      <!-- Loading skeleton -->
      <div v-if="isLoading" class="mx-auto w-full">
        <div
          :class="[
            isMainCart && windowWidth >= breakpoints.xxl
              ? 'grid grid-cols-3 gap-2 pt-2'
              : 'js-slider flex cursor-grab snap-x snap-mandatory gap-2 overflow-x-auto px-4 pt-2 [-ms-overflow-style:none] [scrollbar-width:none] active:cursor-grabbing [&::-webkit-scrollbar]:hidden'
          ]"
        >
          <div
            v-for="i in 3"
            :key="i"
            :class="[
              isMainCart && windowWidth >= breakpoints.xxl
                ? 'flex select-none items-center justify-center rounded-md border border-gray-200 bg-gray-50 p-2 text-sm'
                : 'flex shrink-0 basis-[330px] select-none items-center justify-center rounded-md border border-gray-200 bg-gray-50 p-2 text-sm'
            ]"
          >
            <div class="flex w-full items-center justify-between gap-3">
              <!-- Image skeleton -->
              <div class="flex min-w-0 flex-1 items-start gap-2">
                <div
                  class="size-20 shrink-0 animate-pulse rounded-md bg-gray-200"
                ></div>

                <!-- Product details skeleton -->
                <div class="my-auto min-w-0 flex-1">
                  <div
                    class="mb-1 h-3 w-full animate-pulse rounded bg-gray-200"
                  ></div>
                  <div
                    class="h-3 w-4/5 animate-pulse rounded bg-gray-200"
                  ></div>
                  <div
                    class="mt-2 h-3 w-1/2 animate-pulse rounded bg-gray-200"
                  ></div>
                </div>
              </div>

              <!-- Button skeleton -->
              <div class="shrink-0">
                <div
                  class="h-9 w-16 animate-pulse rounded-md bg-gray-200"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actual content -->
      <div
        v-else-if="products.length > 0"
        class="js-reward-collection-container mx-auto w-full"
      >
        <!-- Main cart: grid layout when >= 1280px -->
        <div
          v-if="isMainCart && windowWidth >= breakpoints.xxl"
          class="grid grid-cols-3 gap-2 pt-2"
        >
          <div v-for="(product, index) in products" :key="product.id">
            <RewardProductSlide
              :product="product"
              :index="index"
              :is-disabled="isAddingGift"
              @add-to-cart="handleAddToCart"
            />
          </div>
        </div>

        <!-- Swiper: for mini cart always, or main cart when < 1280px -->
        <swiper-container
          v-else-if="shouldUseSwiper"
          ref="swiperEl"
          init="false"
          class="js-gift-swiper w-full cursor-grab active:cursor-grabbing"
        >
          <swiper-slide v-for="(product, index) in products" :key="product.id">
            <RewardProductSlide
              :product="product"
              :index="index"
              :is-disabled="isAddingGift"
              @add-to-cart="handleAddToCart"
            />
          </swiper-slide>
        </swiper-container>
      </div>
    </div>
  </div>
</template>
